import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import { supabase } from '../../lib/supabase'
import { Button, Input, ColorPresetPicker, DarkModeToggle } from '../../components/ui'
import { ArrowLeft, Palette, Image as ImageIcon, Save, User, MapPin, Plus, X, Upload, Sparkles } from 'lucide-react'

import { useTranslation } from 'react-i18next'

export const ClubSettingsPage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { activeTenant, setActiveTenant, refreshTenants } = useTenant()
    const [settings, setSettings] = useState({
        name: '',
        primary_color: '#21D19F',
        secondary_color: '#45B869',
        color_preset: 'green', // Track which preset is selected
        background_image_url: '',
        background_images: [],
        slideshow_enabled: false,
        referees: [],
        fields: []
    })
    const [loading, setLoading] = useState(false)
    const [newReferee, setNewReferee] = useState('')
    const [newField, setNewField] = useState('')

    useEffect(() => {
        if (activeTenant) {
            setSettings({
                name: activeTenant.name || '',
                primary_color: activeTenant.primary_color || '#21D19F',
                secondary_color: activeTenant.secondary_color || '#45B869',
                background_image_url: activeTenant.background_image_url || '',
                background_images: activeTenant.background_images || [],
                slideshow_enabled: activeTenant.slideshow_enabled || false,
                referees: activeTenant.referees || [],
                fields: activeTenant.fields || []
            })
        }
    }, [activeTenant])

    const handleSave = async (e) => {
        e.preventDefault()
        setLoading(true)

        try {
            console.log('💾 Opslaan gestart met settings:', settings)

            const { data, error } = await supabase
                .from('tenants')
                .update({
                    name: settings.name,
                    primary_color: settings.primary_color,
                    secondary_color: settings.secondary_color,
                    background_image_url: settings.background_image_url,
                    background_images: settings.background_images,
                    slideshow_enabled: settings.slideshow_enabled,
                    referees: settings.referees,
                    fields: settings.fields
                })
                .eq('id', activeTenant.id)
                .select()
                .single()

            if (error) throw error

            console.log('✅ Database update succesvol:', data)

            setActiveTenant(data)

            // Force refresh from database to ensure sync
            await refreshTenants()

            console.log('🔄 Tenants gerefreshed')

            alert(t('club_branding.save_success'))
        } catch (err) {
            console.error('❌ Fout bij opslaan:', err)
            alert(t('common.error') + ': ' + err.message)
        } finally {
            setLoading(false)
        }
    }

    const resetToDefault = async () => {
        const defaultSettings = {
            ...settings,
            primary_color: '#21D19F',
            secondary_color: '#45B869',
            background_image_url: '',
            background_images: [],
            slideshow_enabled: false
        }

        console.log('🔄 Reset naar standaard gestart')

        setSettings(defaultSettings)
        setLoading(true)

        try {
            const { data, error } = await supabase
                .from('tenants')
                .update({
                    primary_color: defaultSettings.primary_color,
                    secondary_color: defaultSettings.secondary_color,
                    background_image_url: defaultSettings.background_image_url,
                    background_images: defaultSettings.background_images,
                    slideshow_enabled: defaultSettings.slideshow_enabled
                })
                .eq('id', activeTenant.id)
                .select()
                .single()

            if (error) throw error

            console.log('✅ Reset succesvol:', data)

            setActiveTenant(data)

            // Force refresh from database
            await refreshTenants()

            console.log('🔄 Tenants gerefreshed na reset')

            alert(t('club_branding.reset_success'))
        } catch (err) {
            console.error('❌ Fout bij resetten:', err)
            alert(t('common.error') + ': ' + err.message)
        } finally {
            setLoading(false)
        }
    }

    const addReferee = () => {
        if (newReferee.trim()) {
            setSettings({ ...settings, referees: [...settings.referees, newReferee.trim()] })
            setNewReferee('')
        }
    }

    const removeReferee = (index) => {
        setSettings({ ...settings, referees: settings.referees.filter((_, i) => i !== index) })
    }

    const addField = () => {
        if (newField.trim()) {
            setSettings({ ...settings, fields: [...settings.fields, newField.trim()] })
            setNewField('')
        }
    }

    const removeField = (index) => {
        setSettings({ ...settings, fields: settings.fields.filter((_, i) => i !== index) })
    }

    return (
        <div className="min-h-screen">
            <div className="premium-container">
                {/* Header Section - Premium Hero */}
                <div className="mb-12">
                    <Button
                        variant="ghost"
                        onClick={() => navigate('/admin/dashboard')}
                        className="mb-6"
                        icon={<ArrowLeft size={18} />}
                    >
                        {t('club_branding.back_to_dashboard')}
                    </Button>

                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6 mb-8">
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="p-3 rounded-2xl bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg shadow-orange-500/30">
                                    <Sparkles className="text-white" size={28} />
                                </div>
                                <h1 className="text-5xl font-bold bg-gradient-to-br from-gray-900 via-gray-800 to-gray-600 bg-clip-text text-transparent">
                                    {t('club_branding.title')}
                                </h1>
                            </div>
                            <p className="text-lg text-gray-600 leading-relaxed">
                                {t('club_branding.subtitle')}
                            </p>
                        </div>

                        <Button
                            onClick={resetToDefault}
                            variant="outline"
                            icon={<ArrowLeft size={18} />}
                            disabled={loading}
                        >
                            {t('club_branding.reset_default')}
                        </Button>
                    </div>

                    {/* Database Warning */}
                    {activeTenant && activeTenant.referees === undefined && (
                        <div className="glass-strong rounded-2xl p-8 border-l-4 border-red-500 flex items-start gap-6 animate-float">
                            <div className="p-3 bg-red-100 rounded-2xl">
                                <X className="text-red-600" size={28} />
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-red-900 mb-3">{t('club_branding.db_warning_title')}</h3>
                                <p className="text-red-700 mb-6 leading-relaxed">
                                    {t('club_branding.db_warning_text')}
                                </p>
                                <div className="bg-gray-900 rounded-xl p-6 mb-6 overflow-x-auto shadow-lg">
                                    <code className="text-green-400 text-sm font-mono whitespace-pre">
                                        {`ALTER TABLE tenants 
ADD COLUMN IF NOT EXISTS referees TEXT[] DEFAULT '{}',
ADD COLUMN IF NOT EXISTS fields TEXT[] DEFAULT '{}';`}
                                    </code>
                                </div>
                                <Button
                                    onClick={() => window.open('https://supabase.com/dashboard/project/_/sql', '_blank')}
                                    variant="danger"
                                >
                                    {t('club_branding.db_warning_btn')}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                <form onSubmit={handleSave} className="space-y-8">
                    {/* Club Name Card */}
                    <div className="glass-strong premium-card rounded-2xl hover-lift">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                                <User className="text-white" size={24} />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">{t('club_branding.section_name')}</h2>
                        </div>
                        <Input
                            label={t('club_branding.section_name')}
                            value={settings.name}
                            onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                            placeholder={t('club_branding.placeholder_name')}
                            required
                        />
                    </div>

                    {/* Colors Card */}
                    <div className="glass-strong premium-card rounded-2xl hover-lift">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-purple-400 to-purple-600">
                                <Palette className="text-white" size={24} />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-3xl font-bold text-gray-900">{t('club_branding.section_colors')}</h2>
                                <p className="text-gray-600 mt-1">{t('club_branding.colors_desc')}</p>
                            </div>
                        </div>

                        <ColorPresetPicker
                            selectedPreset={settings.color_preset}
                            onSelectPreset={(preset) => {
                                setSettings({
                                    ...settings,
                                    primary_color: preset.primary,
                                    secondary_color: preset.secondary,
                                    color_preset: preset.id
                                })
                            }}
                        />
                    </div>

                    {/* Background & Style Card */}
                    <div className="glass-strong premium-card rounded-2xl hover-lift">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-pink-400 to-pink-600">
                                <ImageIcon className="text-white" size={24} />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-3xl font-bold text-gray-900">{t('club_branding.section_background')}</h2>
                                <p className="text-gray-600 mt-1">{t('club_branding.background_desc')}</p>
                            </div>
                        </div>

                        {/* Slideshow Toggle */}
                        <div className="flex items-center justify-between bg-white/30 backdrop-blur-sm p-6 rounded-2xl mb-8 border border-white/40 hover:bg-white/40 transition-all">
                            <div>
                                <h3 className="font-bold text-gray-900 text-lg">{t('club_branding.slideshow_label')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{t('club_branding.slideshow_desc')}</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={settings.slideshow_enabled || false}
                                    onChange={(e) => setSettings({ ...settings, slideshow_enabled: e.target.checked })}
                                    className="sr-only peer"
                                />
                                <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[var(--primary)]/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-[var(--primary)] peer-checked:to-[var(--secondary)] shadow-inner"></div>
                            </label>
                        </div>

                        {/* Image Gallery */}
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
                            {(settings.background_images || []).map((img, index) => (
                                <div key={index} className="relative group aspect-video rounded-2xl overflow-hidden shadow-lg bg-white/20 border-2 border-transparent hover:border-[var(--primary)] transition-all hover:scale-105">
                                    <img src={img} className="w-full h-full object-cover" alt={`Background ${index + 1}`} />
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const newImages = (settings.background_images || []).filter((_, i) => i !== index)
                                            setSettings({ ...settings, background_images: newImages, background_image_url: newImages[0] || '' })
                                        }}
                                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600 hover:scale-110"
                                    >
                                        <X size={14} />
                                    </button>
                                </div>
                            ))}

                            {/* Upload Button */}
                            {(settings.background_images || []).length < 5 && (
                                <label className="flex flex-col items-center justify-center aspect-video border-2 border-dashed border-white/30 rounded-2xl cursor-pointer hover:bg-white/20 hover:border-[var(--primary)] transition-all group">
                                    <Upload className="text-gray-400 mb-2 group-hover:text-[var(--primary)] transition-colors" size={28} />
                                    <span className="text-xs text-gray-600 font-semibold">{t('club_branding.upload_label')}</span>
                                    <span className="text-[10px] text-gray-400 mt-1">{t('club_branding.upload_remaining', { count: 5 - (settings.background_images || []).length })}</span>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept="image/*"
                                        onChange={async (e) => {
                                            const file = e.target.files[0]
                                            if (!file) return

                                            try {
                                                setLoading(true)
                                                const fileExt = file.name.split('.').pop()
                                                const fileName = `${activeTenant.id}-${Date.now()}.${fileExt}`
                                                const filePath = `${fileName}`

                                                const { error: uploadError } = await supabase.storage
                                                    .from('club-branding')
                                                    .upload(filePath, file)

                                                if (uploadError) throw uploadError

                                                const { data: { publicUrl } } = supabase.storage
                                                    .from('club-branding')
                                                    .getPublicUrl(filePath)

                                                const currentImages = settings.background_images || []
                                                const newImages = [...currentImages, publicUrl]

                                                setSettings({
                                                    ...settings,
                                                    background_images: newImages,
                                                    background_image_url: newImages[0] || ''
                                                })
                                            } catch (err) {
                                                console.error(err)
                                                alert(t('club_branding.upload_failed'))
                                            } finally {
                                                setLoading(false)
                                            }
                                        }}
                                    />
                                </label>
                            )}
                        </div>

                        {/* Gradient Presets */}
                        <div>
                            <label className="label-premium mb-4 block">{t('club_branding.gradient_label')}</label>
                            <div className="flex gap-3 overflow-x-auto pb-2">
                                <button type="button" onClick={() => setSettings({ ...settings, background_image_url: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' })} className="w-16 h-16 rounded-2xl ring-2 ring-white shadow-lg flex-shrink-0 hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' }} title="Purple"></button>
                                <button type="button" onClick={() => setSettings({ ...settings, background_image_url: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' })} className="w-16 h-16 rounded-2xl ring-2 ring-white shadow-lg flex-shrink-0 hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 100%)' }} title="Pink"></button>
                                <button type="button" onClick={() => setSettings({ ...settings, background_image_url: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)' })} className="w-16 h-16 rounded-2xl ring-2 ring-white shadow-lg flex-shrink-0 hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #1F2937 0%, #374151 100%)' }} title="Dark"></button>
                                <button type="button" onClick={() => setSettings({ ...settings, background_image_url: 'linear-gradient(135deg, #0EA5E9 0%, #3B82F6 100%)' })} className="w-16 h-16 rounded-2xl ring-2 ring-white shadow-lg flex-shrink-0 hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #3B82F6 100%)' }} title="Blue"></button>
                                <button type="button" onClick={() => setSettings({ ...settings, background_image_url: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)' })} className="w-16 h-16 rounded-2xl ring-2 ring-white shadow-lg flex-shrink-0 hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #F97316 0%, #FB923C 100%)' }} title="Orange"></button>
                                <button type="button" onClick={() => setSettings({ ...settings, background_image_url: 'linear-gradient(135deg, #21D19F 0%, #45B869 100%)' })} className="w-16 h-16 rounded-2xl ring-2 ring-white shadow-lg flex-shrink-0 hover:scale-110 transition-transform" style={{ background: 'linear-gradient(135deg, #21D19F 0%, #45B869 100%)' }} title="Green"></button>
                            </div>
                        </div>
                    </div>

                    {/* Referees Card */}
                    <div className="glass-strong premium-card rounded-2xl hover-lift">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-green-400 to-green-600">
                                <User className="text-white" size={24} />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-3xl font-bold text-gray-900">{t('club_branding.section_referees')}</h2>
                                <p className="text-gray-600 mt-1">{t('club_branding.referees_desc')}</p>
                            </div>
                        </div>

                        <div className="flex gap-3 mb-6">
                            <Input
                                value={newReferee}
                                onChange={(e) => setNewReferee(e.target.value)}
                                placeholder={t('club_branding.placeholder_referee')}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addReferee())}
                            />
                            <Button type="button" onClick={addReferee} icon={<Plus size={20} />}>
                                {t('club_branding.btn_add')}
                            </Button>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            {settings.referees.map((referee, index) => (
                                <div key={index} className="flex items-center gap-2 bg-gradient-to-r from-blue-100 to-blue-200 text-blue-800 px-4 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all">
                                    <User size={16} />
                                    <span className="font-medium">{referee}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeReferee(index)}
                                        className="hover:text-red-600 transition-colors ml-1"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                            {settings.referees.length === 0 && (
                                <p className="text-gray-500 text-sm italic">{t('club_branding.no_referees')}</p>
                            )}
                        </div>
                    </div>

                    {/* Fields Card */}
                    <div className="glass-strong premium-card rounded-2xl hover-lift">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="p-2 rounded-xl bg-gradient-to-br from-teal-400 to-teal-600">
                                <MapPin className="text-white" size={24} />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-3xl font-bold text-gray-900">{t('club_branding.section_fields')}</h2>
                                <p className="text-gray-600 mt-1">{t('club_branding.fields_desc')}</p>
                            </div>
                        </div>

                        <div className="flex gap-3 mb-6">
                            <Input
                                value={newField}
                                onChange={(e) => setNewField(e.target.value)}
                                placeholder={t('club_branding.placeholder_field')}
                                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addField())}
                            />
                            <Button type="button" onClick={addField} icon={<Plus size={20} />}>
                                {t('club_branding.btn_add')}
                            </Button>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            {settings.fields.map((field, index) => (
                                <div key={index} className="flex items-center gap-2 bg-gradient-to-r from-green-100 to-green-200 text-green-800 px-4 py-2.5 rounded-xl shadow-sm hover:shadow-md transition-all">
                                    <MapPin size={16} />
                                    <span className="font-medium">{field}</span>
                                    <button
                                        type="button"
                                        onClick={() => removeField(index)}
                                        className="hover:text-red-600 transition-colors ml-1"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>
                            ))}
                            {settings.fields.length === 0 && (
                                <p className="text-gray-500 text-sm italic">{t('club_branding.no_fields')}</p>
                            )}
                        </div>
                    </div>

                    {/* Preview Card */}
                    <div className="glass-strong premium-card rounded-2xl hover-lift">
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">{t('club_branding.preview_title')}</h2>
                        <p className="text-gray-600 mb-8">{t('club_branding.preview_desc')}</p>

                        <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-12 text-center border border-white/30">
                            <h3 className="text-4xl font-bold mb-8" style={{ color: settings.primary_color }}>
                                {settings.name || t('club_branding.preview_club_name')}
                            </h3>
                            <div className="flex flex-wrap justify-center gap-4">
                                <button
                                    className="px-8 py-4 rounded-2xl text-white font-semibold shadow-xl hover:scale-105 transition-transform"
                                    style={{ background: `linear-gradient(135deg, ${settings.primary_color}, ${settings.secondary_color})` }}
                                >
                                    {t('club_branding.preview_btn_primary')}
                                </button>
                                <button
                                    className="px-8 py-4 rounded-2xl text-white font-semibold shadow-xl hover:scale-105 transition-transform"
                                    style={{ backgroundColor: settings.secondary_color }}
                                >
                                    {t('club_branding.preview_btn_secondary')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex justify-end gap-4 pt-8">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => navigate('/admin/dashboard')}
                            size="lg"
                        >
                            {t('club_branding.btn_cancel')}
                        </Button>
                        <Button
                            type="submit"
                            disabled={loading}
                            icon={<Save size={20} />}
                            size="lg"
                        >
                            {loading ? t('club_branding.saving') : t('club_branding.btn_save')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    )
}
